package org.catharsis.spendtel.feature.legal

object SpendTelPrivacyPolicy {
    fun document(): LegalDocument = LegalDocument(
        type = LegalDocumentType.PrivacyPolicy,
        title = "Privacy Policy",
        subtitle = "How ${LegalConstants.COMPANY_NAME} collects, uses, and protects information when you use ${LegalConstants.PRODUCT_NAME}.",
        lastUpdated = LegalConstants.EFFECTIVE_DATE,
        sections = listOf(
            LegalSection(
                title = "1. Introduction",
                paragraphs = listOf(
                    "${LegalConstants.COMPANY_NAME} (\"${LegalConstants.COMPANY_NAME}\", \"we\", \"us\", or \"our\") operates ${LegalConstants.PRODUCT_NAME} (the \"Service\"), available at ${LegalConstants.WEBSITE} and through our mobile applications.",
                    "This Privacy Policy describes how we collect, use, disclose, and safeguard personal information when you access or use the Service. By using the Service, you acknowledge that you have read this Privacy Policy.",
                    "This Privacy Policy is incorporated into our Terms and Conditions. Capitalized terms not defined here have the meanings given in the Terms.",
                ),
            ),
            LegalSection(
                title = "2. Information We Collect",
                paragraphs = listOf(
                    "We collect information in the following categories:",
                ),
                bullets = listOf(
                    "Account information: name, email address, authentication identifiers, profile preferences (such as financial goals), and account settings.",
                    "Financial statement data: bank or card statements and related files you upload (for example PDF or CSV), including transaction descriptions, amounts, dates, merchant names, and account metadata contained in those files.",
                    "Generated report data: spending summaries, narratives, insights, categorizations, and other outputs we create from your uploaded statements.",
                    "Payment and billing information: purchase history, report credits, subscription or one-time payment status, and limited payment metadata processed by our payment providers. We do not store full payment card numbers on our servers.",
                    "Usage and device information: app interactions, feature usage, log data, IP address, browser or device type, operating system, and diagnostic or crash data.",
                    "Communications: messages you send to us for support or legal requests.",
                ),
            ),
            LegalSection(
                title = "3. How We Use Information",
                paragraphs = listOf(
                    "We use personal information to:",
                ),
                bullets = listOf(
                    "Provide, operate, maintain, and improve the Service.",
                    "Authenticate users and manage accounts.",
                    "Process uploaded statements and generate spending reports and insights.",
                    "Process payments, manage report credits, and prevent fraud or abuse.",
                    "Respond to support requests and communicate about the Service.",
                    "Send service-related notices (for example security, billing, or policy updates).",
                    "Analyze usage to improve performance, reliability, and user experience.",
                    "Comply with law, enforce our Terms, and protect our rights, users, and the public.",
                ),
            ),
            LegalSection(
                title = "4. Legal Bases for Processing (EEA/UK Users)",
                paragraphs = listOf(
                    "If you are located in the European Economic Area or the United Kingdom, we process personal data based on: (a) performance of a contract with you; (b) our legitimate interests in operating and improving the Service, subject to your rights; (c) compliance with legal obligations; and (d) your consent where required (for example optional marketing).",
                ),
            ),
            LegalSection(
                title = "5. How We Share Information",
                paragraphs = listOf(
                    "We do not sell your personal information. We may share information in the following circumstances:",
                ),
                bullets = listOf(
                    "Service providers: cloud hosting, authentication, storage, analytics, payment processing, email delivery, and other vendors that help us operate the Service under contractual confidentiality and security obligations.",
                    "Professional advisors: lawyers, accountants, auditors, or insurers when reasonably necessary.",
                    "Business transfers: in connection with a merger, acquisition, financing, reorganization, or sale of assets, subject to this Privacy Policy unless you are notified otherwise.",
                    "Legal and safety: when required by law, regulation, legal process, or governmental request, or when we believe disclosure is necessary to protect rights, safety, or property.",
                    "With your direction: when you ask us to share information or otherwise consent.",
                ),
            ),
            LegalSection(
                title = "6. Financial Information Notice",
                paragraphs = listOf(
                    "${LegalConstants.PRODUCT_NAME} is a personal finance insights tool, not a bank, lender, broker-dealer, investment adviser, or credit bureau. We are not providing financial, legal, tax, or investment advice.",
                    "When you upload financial statements, you authorize us to access, store, process, and analyze that information solely to provide the Service. You should redact or avoid uploading information you do not want processed.",
                    "If our activities are subject to financial privacy laws applicable to your jurisdiction, we will handle nonpublic personal information in accordance with those laws and this Policy.",
                ),
            ),
            LegalSection(
                title = "7. Data Retention",
                paragraphs = listOf(
                    "We retain personal information for as long as your account is active or as needed to provide the Service, comply with legal obligations, resolve disputes, and enforce our agreements.",
                    "You may delete individual reports or delete your account through Settings. When you delete your account, we delete or de-identify associated profile data, reports, and uploaded statements within a reasonable period, subject to backup retention and legal retention requirements.",
                ),
            ),
            LegalSection(
                title = "8. Security",
                paragraphs = listOf(
                    "We use administrative, technical, and organizational measures designed to protect personal information, including encryption in transit and access controls.",
                    "No method of transmission or storage is completely secure. We cannot guarantee absolute security of your information.",
                ),
            ),
            LegalSection(
                title = "9. Your Privacy Rights",
                paragraphs = listOf(
                    "Depending on where you live, you may have rights to access, correct, delete, or obtain a copy of your personal information, to opt out of certain processing, or to appeal our response to a request.",
                    "California residents may have additional rights under the CCPA/CPRA, including the right to know, delete, correct, and limit use of sensitive personal information. We do not sell or share personal information for cross-context behavioral advertising.",
                    "To exercise your rights, email ${LegalConstants.PRIVACY_EMAIL}. We may verify your request before responding. You may designate an authorized agent where permitted by law.",
                ),
            ),
            LegalSection(
                title = "10. Cookies and Similar Technologies",
                paragraphs = listOf(
                    "Our website may use cookies, local storage, and similar technologies for authentication, preferences, analytics, and security. You can control cookies through your browser settings, but some features may not function properly if cookies are disabled.",
                ),
            ),
            LegalSection(
                title = "11. Third-Party Services",
                paragraphs = listOf(
                    "The Service may link to or integrate with third-party services (for example Google sign-in, Firebase, or payment processors). Their handling of information is governed by their own policies. We are not responsible for third-party privacy practices.",
                ),
            ),
            LegalSection(
                title = "12. Children’s Privacy",
                paragraphs = listOf(
                    "The Service is not directed to anyone under 18, and we do not knowingly collect personal information from children. If you believe a child has provided us information, contact ${LegalConstants.PRIVACY_EMAIL} and we will take appropriate steps to delete it.",
                ),
            ),
            LegalSection(
                title = "13. International Transfers",
                paragraphs = listOf(
                    "We are based in the United States. If you access the Service from outside the U.S., your information may be transferred to, stored in, and processed in the U.S. or other countries that may have different data protection laws than your country.",
                ),
            ),
            LegalSection(
                title = "14. Changes to This Policy",
                paragraphs = listOf(
                    "We may update this Privacy Policy from time to time. We will post the updated version on the Service and update the \"Last updated\" date. Material changes may be communicated by email or in-app notice where required by law. Continued use after changes become effective constitutes acceptance of the updated Policy.",
                ),
            ),
            LegalSection(
                title = "15. Contact Us",
                paragraphs = listOf(
                    "Questions about this Privacy Policy or our privacy practices:",
                    "${LegalConstants.COMPANY_NAME}\nEmail: ${LegalConstants.PRIVACY_EMAIL}\nWebsite: ${LegalConstants.WEBSITE}",
                ),
            ),
        ),
    )
}
